import { Card, CardHeader, Divider, Box, Typography, Backdrop, CircularProgress, useTheme } from "@mui/material"
import { ListContextProvider, useListController } from "ra-core"
import { FC, useMemo } from "react"
import { useSelector } from "react-redux"
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import { RecPatch } from "@/dataProvider/tools"
import { ErrorLogDto, OnDataLogLevel } from "@/gen/api/secureai"
import { getUserProfile } from "@/reducers/userProfile"
import { useUserTimeFormat } from "@/utils/userTimeFormat"
import { FunctionField, TextField } from "../field"
import DateField2 from "../field/DateField2"
import { usePopTimeRangePicker, PopTimeRangeKind, displayTimeRangeLabel } from "../input/PopTimeRangePicker"
import SearchCriteriaChips, { ChipItem } from "../input/SearchCriteriaChips"
import { Datagrid } from "../list/datagrid"
import Scrollbar from "../Scrollbar"

const ViewLogsCard : FC<{clientId:string}> = ({
  clientId,
}) => {
  const userProfile = useSelector(getUserProfile)
  const format = useUserTimeFormat()
  const { PopTimeRangePicker, open, states: tStates } = usePopTimeRangePicker({
    label: 'Time Range',
    onChange: ({kind, start, end}) => {
      // setSearchTime(start, end)
    },
    PopoverProps: {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 'right' },
    },
    ianaTimeZone: userProfile?.ianaTimeZone,
  })
  const listCtrl = useListController({ basePath: '', 
    resource: clientId && (tStates.start || tStates.end) ? 'secure-ai-error-logs' : 'noop', 
    filter: {clientId, fromTs: tStates.start, toTs: tStates.end},
    sort: {
      field: 'reportTime',
      order: 'DESC'
    }
  })

  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (tStates) {
      if (tStates.start || tStates.end) {
        arr.push([{
          id: 'tr',
          label: 'Time Range',
          content: 
            tStates.kind !== PopTimeRangeKind.Custom
            ? (<code style={{ textTransform: 'capitalize', marginLeft: '8px' }}>within {displayTimeRangeLabel(tStates.kind)}</code> as any)
            : !tStates.end ? `Starts from ${format(tStates.start!, 'Pp')}`
            : !tStates.start ? `Up until ${format(tStates.end!, 'Pp')}`
            : `${format(tStates.start, 'Pp')} - ${format(tStates.end, 'Pp')}`
            ,
          delete: () => open(),
        }])
      }
    }
    return arr
  }, [format, open, tStates])

  const theme = useTheme()
  const cardBgFix = theme.palette.mode === 'dark' && {
    sx: {
      bgcolor: '#121b25',
    }
  }
  
  return (
    <ListContextProvider value={listCtrl}>
      <Card {...cardBgFix}>
        <CardHeader title="View Logs" action={
          PopTimeRangePicker
        } />
        <Divider />
        <SearchCriteriaChips criteriaChips={criteriaChips} />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150, position: 'relative', ...(listCtrl.loading && { minHeight: 48 }) }}>
            <Datagrid
              sx={{
                '& td div.message': { 
                  width: '700px',
                  overflowY: 'auto',
                  px: 1,
                  bgcolor: 'background.default',
                },
              }}
            >
              <DateField2 label="Report Time" source="reportTime" sortable={false} showTime={true} />
              <FunctionField label="Log Level" source="logLevel" sortable={false} 
                render={(d?:ErrorLogDto&RecPatch) =>
                  d &&
                  <Typography component="span" variant="caption" 
                    color={
                      d.logLevel !== OnDataLogLevel.Error ? undefined
                      : theme.palette.mode === 'dark' 
                        ? theme.palette.error.light 
                        : theme.palette.error.main }
                  >
                    {OnDataLogLevel[d.logLevel ?? 0]}
                  </Typography>
                }
              />
              <FunctionField label="Message" source="message" sortable={false} className="message" 
                render={(d?:any) =>
                  <Box className="message">
                    <Typography component="span" variant="caption" >
                      <pre>
                        <code>
                          {d.message}
                        </code>
                      </pre>
                    </Typography>
                  </Box>
                }
              />
              <TextField label="Error Type" source="errorType" sortable={false} />
            </Datagrid>
            <Backdrop open={listCtrl.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute' }} invisible>
              <CircularProgress />
            </Backdrop>
          </Box>
        </Scrollbar>
        {//@ts-ignore
          <DefaultPagination {...listCtrl as any} />}
      </Card>
    </ListContextProvider>
  )
}

export default ViewLogsCard;
